import { useState } from "react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Truck, CheckCircle2, Package, Clock } from "lucide-react";

const STAGES = ["unfulfilled", "processing", "shipped", "delivered"];
const LABELS = { unfulfilled: "Order received", processing: "Preparing", shipped: "Shipped", delivered: "Delivered" };
const STAGE_ICONS = [CheckCircle2, Clock, Truck, Package];

export default function OrderTracking() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  const search = async (e) => {
    e.preventDefault();
    setErr("");
    setOrder(null);
    try {
      const r = await api.get("/orders/track", { params: { order_number: orderNumber, email } });
      setOrder(r.data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not find order");
    }
  };

  const stageIdx = order ? Math.max(0, STAGES.indexOf(order.fulfillment_status)) : -1;

  return (
    <div className="fade-in" data-testid="page-track">
      <SEO title="Track your order" path="/track" />
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16">
        <p className="eyebrow mb-4">Order tracking</p>
        <h1 className="text-h1 text-[#1D1D1F]">Where’s my order?</h1>
        <p className="mt-4 text-[#86868B]">Enter your order number and email to see live status and tracking.</p>

        <form onSubmit={search} className="mt-10 grid sm:grid-cols-[1fr_1fr_auto] gap-3">
          <input
            required
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
            placeholder="CMP-XXXXXXXX"
            data-testid="track-order-number"
            className="h-12 px-4 rounded-xl bg-[#F5F5F7] border border-transparent focus:border-[#0066FF] focus:bg-white focus:outline-none"
          />
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@creator.com"
            data-testid="track-email"
            className="h-12 px-4 rounded-xl bg-[#F5F5F7] border border-transparent focus:border-[#0066FF] focus:bg-white focus:outline-none"
          />
          <button type="submit" className="btn-primary" data-testid="track-search">Track</button>
        </form>

        {err && <p className="mt-6 text-sm text-[#FF3B30]" data-testid="track-error">{err}</p>}

        {order && (
          <div className="mt-12 space-y-8" data-testid="track-result">
            <div className="soft-card p-6 md:p-8">
              <p className="text-xs text-[#86868B]">Order</p>
              <p className="font-display text-2xl font-bold">#{order.order_number}</p>
              <p className="text-sm text-[#86868B] mt-1">Placed {new Date(order.created_at).toLocaleDateString()}</p>

              <div className="mt-8 grid grid-cols-4 gap-2">
                {STAGES.map((s, i) => {
                  const active = i <= stageIdx;
                  const Icon = STAGE_ICONS[i];
                  return (
                    <div key={s} className="text-center">
                      <div className={`w-10 h-10 mx-auto rounded-full grid place-items-center ${active ? "bg-[#0066FF] text-white" : "bg-[#E5E5EA] text-[#86868B]"}`}>
                        <Icon size={16} />
                      </div>
                      <p className={`mt-2 text-xs ${active ? "text-[#1D1D1F] font-semibold" : "text-[#86868B]"}`}>{LABELS[s]}</p>
                    </div>
                  );
                })}
              </div>

              {order.tracking_number && (
                <div className="mt-8 p-4 bg-white rounded-2xl border border-black/5">
                  <p className="text-xs text-[#86868B]">Tracking</p>
                  <p className="font-mono-tech text-sm">{order.shipping_carrier} · {order.tracking_number}</p>
                </div>
              )}
            </div>

            <div className="soft-card p-6 md:p-8">
              <p className="eyebrow mb-4">Items</p>
              <div className="space-y-4">
                {order.lines.map((l) => (
                  <div key={`${l.variant_name}-${l.product_name}`} className="flex gap-3 items-center">
                    {l.image && <img src={l.image} alt="" className="w-16 h-16 rounded-xl object-cover bg-white" />}
                    <div className="flex-1">
                      <p className="font-medium">{l.product_name}</p>
                      <p className="text-xs text-[#86868B]">{l.variant_name} × {l.quantity}</p>
                    </div>
                    <p className="text-sm font-semibold">${l.line_total.toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-right text-sm">Total <span className="font-display text-lg font-bold ml-2">${order.total.toFixed(2)}</span></p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
